import { useState } from 'react';
import clsx from 'clsx';
import { ArrowUpIcon, ArrowDownIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { BLOCK_CATALOG, blockDef, makeBlock, type PlanBlock } from '../../lib/opBlocks';
import { PHASE_META } from '../../types/domain';

interface Props {
  blocks: PlanBlock[];
  onChange: (blocks: PlanBlock[]) => void;
  disabled?: boolean;
}

// Pragmatic form-based sequence editor: add / remove / reorder / edit rows.
export default function BlockSequenceEditor({ blocks, onChange, disabled }: Props) {
  const [addId, setAddId] = useState(BLOCK_CATALOG[0].blockId);

  function addRow(blockId: string) {
    onChange([...blocks, makeBlock(blockId)]);
  }
  function update(i: number, patch: Partial<PlanBlock>) {
    onChange(blocks.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  }
  function remove(i: number) {
    onChange(blocks.filter((_, idx) => idx !== i));
  }
  function move(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  }

  return (
    <div className="space-y-2">
      <div className="space-y-2">
        {blocks.map((b, i) => {
          const def = blockDef(b.blockId);
          const phase = b.phase ?? def?.phase;
          return (
            <div key={i} className="rounded-lg border border-command-border bg-command-bg p-2.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] text-gray-500 font-tactical w-6">{i + 1}</span>
                <span className="text-base" aria-hidden>{def?.icon ?? '📄'}</span>
                <select
                  aria-label={`Block ${i + 1} type`}
                  value={b.blockId}
                  disabled={disabled}
                  onChange={(e) => {
                    const nd = blockDef(e.target.value);
                    update(i, { blockId: e.target.value, phase: nd?.phase });
                  }}
                  className="px-2 py-1 rounded bg-command-panel border border-command-border text-xs text-gray-100"
                >
                  {BLOCK_CATALOG.map((d) => <option key={d.blockId} value={d.blockId}>{d.label}</option>)}
                </select>
                {phase && (
                  <span
                    className="px-1.5 py-0.5 rounded text-[9px] font-bold font-tactical"
                    style={{ color: PHASE_META[phase].color, borderColor: PHASE_META[phase].color, border: '1px solid' }}
                  >
                    {PHASE_META[phase].label}
                  </span>
                )}
                <label className="ml-auto flex items-center gap-1 text-[10px] text-gray-500">
                  weight
                  <input
                    type="number"
                    min={1}
                    max={99}
                    value={b.weight}
                    disabled={disabled}
                    onChange={(e) => update(i, { weight: Math.max(1, Number(e.target.value) || 1) })}
                    className="w-14 px-1.5 py-1 rounded bg-command-panel border border-command-border text-xs text-gray-100"
                  />
                </label>
                <div className="flex items-center gap-1">
                  <button type="button" disabled={disabled || i === 0} onClick={() => move(i, -1)}
                    className="p-1 rounded hover:bg-command-border/60 disabled:opacity-30" aria-label="Move up">
                    <ArrowUpIcon className="w-4 h-4 text-gray-400" />
                  </button>
                  <button type="button" disabled={disabled || i === blocks.length - 1} onClick={() => move(i, 1)}
                    className="p-1 rounded hover:bg-command-border/60 disabled:opacity-30" aria-label="Move down">
                    <ArrowDownIcon className="w-4 h-4 text-gray-400" />
                  </button>
                  <button type="button" disabled={disabled} onClick={() => remove(i)}
                    className="p-1 rounded hover:bg-severity-critical/20 disabled:opacity-30" aria-label="Remove block">
                    <TrashIcon className="w-4 h-4 text-severity-critical" />
                  </button>
                </div>
              </div>
              <input
                type="text"
                value={String(b.params.note ?? '')}
                disabled={disabled}
                placeholder={def?.hint ?? 'Notes / parameters…'}
                onChange={(e) => update(i, { params: { ...b.params, note: e.target.value } })}
                className="mt-2 w-full px-2 py-1 rounded bg-command-panel border border-command-border text-xs text-gray-200 placeholder-gray-600"
              />
            </div>
          );
        })}
        {blocks.length === 0 && (
          <div className="py-6 text-center text-gray-500 text-sm border border-dashed border-command-border rounded-lg">
            No blocks yet — add the first step of the sequence below.
          </div>
        )}
      </div>

      {!disabled && (
        <div className="flex items-center gap-2">
          <select
            aria-label="Block to add"
            value={addId}
            onChange={(e) => setAddId(e.target.value)}
            className="px-2 py-1.5 rounded bg-command-bg border border-command-border text-xs text-gray-100"
          >
            {BLOCK_CATALOG.map((d) => <option key={d.blockId} value={d.blockId}>{d.icon} {d.label}</option>)}
          </select>
          <button
            type="button"
            onClick={() => addRow(addId)}
            className={clsx('inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold',
              'bg-command-accent/20 text-command-accent border border-command-accent/40 hover:bg-command-accent/30')}
          >
            <PlusIcon className="w-4 h-4" /> Add block
          </button>
        </div>
      )}
    </div>
  );
}
